/**
 * PrintDialog
 *
 * Modal dialog for configuring and executing a map print/export.
 * Supports scale selection, paper size, area mode, multi-page tiling,
 * and optional declination display on the printed output.
 */

import React, { useState, useMemo } from 'react'
import {
  computePrintBounds,
  computeTileGrid,
  formatScaleText,
  getMagneticDeclination,
} from '../utils/printMap.js'

const SCALES = [25000, 50000, 100000, 250000]
const PAPER_OPTIONS = ['letter', 'a4']

/**
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   mapCenter: { lat: number, lng: number },
 *   mapBounds: { north: number, south: number, east: number, west: number } | null,
 * }} props
 */
export function PrintDialog({ open, onClose, mapCenter, mapBounds }) {
  const [scale, setScale] = useState(50000)
  const [paperSize, setPaperSize] = useState('letter')
  const [areaMode, setAreaMode] = useState('current')
  const [multiPage, setMultiPage] = useState(false)
  const [includeDeclination, setIncludeDeclination] = useState(true)

  // Compute print bounds for the current configuration
  const printBounds = useMemo(() => {
    if (!mapCenter) return null
    return computePrintBounds(mapCenter, scale, paperSize)
  }, [mapCenter, scale, paperSize])

  // Compute tile grid when multi-page is enabled
  const tileGrid = useMemo(() => {
    const bounds = areaMode === 'current' ? printBounds : mapBounds
    if (!bounds || !multiPage) return null
    return computeTileGrid(bounds, scale, paperSize)
  }, [areaMode, printBounds, mapBounds, multiPage, scale, paperSize])

  // Check if the area exceeds one page
  const exceedsOnePage = useMemo(() => {
    if (!mapBounds || !printBounds) return false
    const viewWidthDeg = mapBounds.east - mapBounds.west
    const viewHeightDeg = mapBounds.north - mapBounds.south
    const pageWidthDeg = printBounds.east - printBounds.west
    const pageHeightDeg = printBounds.north - printBounds.south
    return viewWidthDeg > pageWidthDeg || viewHeightDeg > pageHeightDeg
  }, [mapBounds, printBounds])

  // Declination for the center point
  const declination = useMemo(() => {
    if (!mapCenter) return null
    return getMagneticDeclination(mapCenter.lat, mapCenter.lng)
  }, [mapCenter])

  // Page count
  const pageCount = tileGrid ? tileGrid.length : 1

  const handlePrint = () => {
    // Add a temporary class for print-specific layout
    document.body.classList.add('printing-map')

    // Store print metadata as a data attribute for CSS @media print
    const meta = document.createElement('meta')
    meta.name = 'print-scale'
    meta.content = String(scale)
    document.head.appendChild(meta)

    window.print()

    // Cleanup after print dialog closes
    document.body.classList.remove('printing-map')
    document.head.removeChild(meta)
    onClose()
  }

  if (!open) return null

  return (
    <div className="print-dialog" role="dialog" aria-label="Print Map">
      <div className="print-dialog-content">
        <h2 className="print-dialog-title">Print Map</h2>

        {/* Scale selector */}
        <div className="print-field">
          <label className="print-label" htmlFor="print-scale">
            Scale
          </label>
          <select
            id="print-scale"
            className="print-select"
            value={scale}
            onChange={(e) => setScale(Number(e.target.value))}
          >
            {SCALES.map((s) => (
              <option key={s} value={s}>
                {formatScaleText(s)}
              </option>
            ))}
          </select>
        </div>

        {/* Paper size */}
        <div className="print-field">
          <label className="print-label" htmlFor="print-paper">
            Paper Size
          </label>
          <select
            id="print-paper"
            className="print-select"
            value={paperSize}
            onChange={(e) => setPaperSize(e.target.value)}
          >
            {PAPER_OPTIONS.map((p) => (
              <option key={p} value={p}>
                {p === 'letter' ? 'Letter (8.5" x 11")' : 'A4 (210mm x 297mm)'}
              </option>
            ))}
          </select>
        </div>

        {/* Area selection */}
        <div className="print-field">
          <label className="print-label" htmlFor="print-area">
            Area
          </label>
          <select
            id="print-area"
            className="print-select"
            value={areaMode}
            onChange={(e) => setAreaMode(e.target.value)}
          >
            <option value="current">Current View</option>
            <option value="custom">Custom Area</option>
          </select>
        </div>

        {/* Multi-page checkbox */}
        <div className="print-field print-field-row">
          <label className="print-checkbox-label">
            <input
              type="checkbox"
              checked={multiPage}
              disabled={!exceedsOnePage}
              onChange={(e) => setMultiPage(e.target.checked)}
            />
            <span>Multi-page tiling</span>
          </label>
          {multiPage && tileGrid && (
            <span className="print-page-count">{pageCount} pages</span>
          )}
        </div>

        {/* Include declination */}
        <div className="print-field print-field-row">
          <label className="print-checkbox-label">
            <input
              type="checkbox"
              checked={includeDeclination}
              onChange={(e) => setIncludeDeclination(e.target.checked)}
            />
            <span>Include declination</span>
          </label>
        </div>

        {/* Preview info */}
        {printBounds && (
          <div className="print-preview-info">
            <div className="print-preview-line">
              Coverage: {printBounds.widthKm.toFixed(1)} km x{' '}
              {printBounds.heightKm.toFixed(1)} km
            </div>
            {declination && includeDeclination && (
              <div className="print-preview-line">
                Declination: {declination.declination.toFixed(1)}&deg;{' '}
                {declination.direction} ({declination.year})
              </div>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div className="print-actions">
          <button
            className="btn-military print-cancel-btn"
            type="button"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="btn-military print-confirm-btn"
            type="button"
            onClick={handlePrint}
          >
            Print
          </button>
        </div>
      </div>
    </div>
  )
}

export default PrintDialog
